import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

import { QueryKey, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';

// Collapse nudge bursts into one refetch (a fresh referral settles once per automated
// step); the max wait bounds how stale a continuously-busy run can look.
const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

/**
 * The WorkflowChannel live-update wiring (OLIO-2602), shared by every query that
 * rides this run's nudge: the API broadcasts an id-only nudge whenever the run
 * settles, and this invalidates `queryKey` so the owning query refetches — the
 * fetch stays the single source of truth.
 *
 * The subscribe-time transmit is skipped only on the FIRST connect and only when
 * that connect actually carries one: creating the subscription transmits, and the
 * query just fetched, so refetching would be a wasted request per page view.
 * Joining an already-live shared subscription transmits nothing — skipping there
 * would swallow the next real broadcast instead (e.g. opening the Activity tab on
 * an already-open referral page). A reconnect's transmit always refetches,
 * converging anything missed while offline.
 */
export default function useWorkflowNudge(id: string | undefined, queryKey: QueryKey) {
  const queryClient = useQueryClient();

  // Recreate the debouncer only when the key's CONTENT changes (the array is a
  // fresh object every render).
  const serializedKey = JSON.stringify(queryKey);
  const nudged = useMemo(
    () =>
      debounce(() => queryClient.invalidateQueries({ queryKey }), NUDGE_DEBOUNCE_MS, { maxWait: NUDGE_MAX_WAIT_MS }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, serializedKey]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  const everConnected = useRef(false);
  const skipNextReceived = useRef(false);

  useWebsocketSubscription({
    channel: 'WorkflowChannel',
    params: { workflow_id: id },
    enabled: !!id,
    onConnected: (meta) => {
      // First connect only — a RECONNECT's transmit still refetches, converging
      // anything missed while offline.
      if (everConnected.current) return;
      everConnected.current = true;
      if (!meta?.joinedExisting) skipNextReceived.current = true;
    },
    onReceived: () => {
      if (skipNextReceived.current) {
        skipNextReceived.current = false;
        return;
      }
      nudged();
    },
  });
}
